import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Seo } from '../components/Seo';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../lib/api';
import { EVENT_TYPES, GUEST_COUNT_TYPES } from '../lib/site';
import { CATEGORIES, packagesFor, priceLabel, hours, SERVICE_ADDONS } from '../lib/packageData';

const CONFIRMATION =
  'Thank you for contacting Mayden Films. Your enquiry has been received. I’ll review the details and respond within two business days. Your date is not reserved until the booking agreement is signed and the 30% booking fee has been received.';

// Top-level services the customer can select (one or many)
const SERVICES = [
  { value: 'glambooth', label: 'Glam Booth' },
  { value: 'photography', label: 'Photography' },
  { value: 'videography', label: 'Videography' },
  { value: 'combined', label: 'Combined Services' },
];

const GLAM_TIERS = [
  { value: 'glambooth_mini', label: 'Mini (from $399)' },
  { value: 'glambooth_signature', label: 'Signature (from $599)' },
  { value: 'glambooth_celebration', label: 'Celebration (from $799)' },
  { value: 'glambooth_ultimate', label: 'Ultimate (from $1,099)' },
];

const initial = {
  firstName: '', lastName: '', email: '', mobile: '',
  eventType: '', eventName: '', eventDate: '', venueName: '', venueSuburb: '', venueAddress: '',
  startTime: '', finishTime: '', guestCount: '',
  services: [], glamTier: '', photoPackage: '', videoPackage: '', selectedUpgrades: [], promoCode: '',
  otherRequirements: '', hearAbout: '',
  contactPermission: false, privacyAck: false, website: '',
};

const Label = ({ children, required }) => (
  <label className="block text-[#EADBC8] text-sm font-medium mb-2">
    {children}{required && <span className="text-[#C5A880]"> *</span>}
  </label>
);

const inputCls = 'bg-[#1A1612] border-[#C5A880]/20 text-[#EADBC8] placeholder:text-gray-500 focus:border-[#C5A880]';
const money = (n) => `$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`;

const Quote = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initial);
  const [packages, setPackages] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [appliedCode, setAppliedCode] = useState('');
  const [promoMsg, setPromoMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { publicApi.getPackages().then((d) => setPackages(d || [])).catch(() => {}); }, []);

  // A live SERVER estimate only applies to Glam Booth tiers. Photography/Videography
  // prices come from the approved package data (shown when a package is picked).
  const pricedService = (() => {
    const s = form.services;
    if (s.length === 1 && s[0] === 'glambooth' && form.glamTier) return form.glamTier;
    return '';
  })();
  const currentPkg = packages.find((p) => p.service_key === pricedService);
  const isPriced = !!pricedService;
  const showGuestCount = GUEST_COUNT_TYPES.includes(form.eventType);
  const selectedCatId = CATEGORIES.find((c) => c.name === form.eventType)?.id;
  const photoOptions = selectedCatId ? packagesFor('Photography', selectedCatId) : [];
  const videoOptions = selectedCatId ? packagesFor('Videography', selectedCatId) : [];
  const selectedPhotoPkg = photoOptions.find((p) => p.id === form.photoPackage);
  const selectedVideoPkg = videoOptions.find((p) => p.id === form.videoPackage);
  const needsEventType = form.services.includes('photography') || form.services.includes('videography');

  const set = (name, value) => setForm((p) => ({ ...p, [name]: value }));
  const handleChange = (e) => set(e.target.name, e.target.value);

  const toggleService = (value) => setForm((p) => {
    const has = p.services.includes(value);
    const services = has ? p.services.filter((x) => x !== value) : [...p.services, value];
    return {
      ...p, services, selectedUpgrades: [], promoCode: '',
      glamTier: value === 'glambooth' && has ? '' : p.glamTier,
      photoPackage: value === 'photography' && has ? '' : p.photoPackage,
      videoPackage: value === 'videography' && has ? '' : p.videoPackage,
    };
  });

  const toggleUpgrade = (name) => setForm((p) => {
    const has = p.selectedUpgrades.includes(name);
    return { ...p, selectedUpgrades: has ? p.selectedUpgrades.filter((x) => x !== name) : [...p.selectedUpgrades, name] };
  });

  const refreshPricing = useCallback(async (code) => {
    if (!pricedService) { setPricing(null); return; }
    try {
      const res = await publicApi.validatePromo(code || '', pricedService, form.selectedUpgrades);
      setPricing(res);
      if (code) {
        if (res.promoApplied) { setAppliedCode(res.promoCode); setPromoMsg(res.message); }
        else { setAppliedCode(''); setPromoMsg(res.message || 'This promo code is not valid.'); }
      }
    } catch { setPricing(null); }
  }, [pricedService, form.selectedUpgrades]);

  // Recompute the estimate whenever the resolved service/upgrades change
  useEffect(() => { refreshPricing(appliedCode); }, [pricedService, form.selectedUpgrades]); // eslint-disable-line

  const applyPromo = () => {
    if (!form.promoCode.trim()) return;
    refreshPricing(form.promoCode.trim());
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) { toast({ title: 'Please choose at least one service', variant: 'destructive' }); return; }
    if (!form.contactPermission || !form.privacyAck) {
      toast({ title: 'Please confirm the two acknowledgements', variant: 'destructive' }); return;
    }
    setSubmitting(true);
    const serviceLabels = form.services.map((v) => SERVICES.find((s) => s.value === v)?.label || v);
    const glamLabel = form.glamTier ? ` — ${GLAM_TIERS.find((t) => t.value === form.glamTier)?.label || ''}` : '';
    const pkgIds = [form.glamTier, form.photoPackage, form.videoPackage].filter((x) => x && x !== 'custom-help');
    const pkgBits = [];
    if (form.glamTier) pkgBits.push(`Glam Booth: ${GLAM_TIERS.find((t) => t.value === form.glamTier)?.label || form.glamTier}`);
    if (form.photoPackage) pkgBits.push(`Photography: ${form.photoPackage === 'custom-help' ? 'Help me choose / custom' : form.photoPackage}`);
    if (form.videoPackage) pkgBits.push(`Videography: ${form.videoPackage === 'custom-help' ? 'Help me choose / custom' : form.videoPackage}`);
    try {
      await publicApi.submitQuote({
        ...form,
        service: pricedService || form.services[0] || 'combined',
        services: serviceLabels.join(', '),
        packageIds: pkgIds.join(', '),
        promoCode: appliedCode,
        packageChoice: serviceLabels.join(', ') + glamLabel + (pkgBits.length ? ` · ${pkgBits.join(' · ')}` : ''),
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: err.message, variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#1F1A17] flex items-center justify-center px-4 pt-24 pb-16">
        <Seo exactTitle="Enquiry Received | Mayden Films" description="Your Mayden Films enquiry has been received." path="/quote" />
        <Card className="bg-[#2A2420] border-[#C5A880]/30 max-w-xl w-full" data-testid="quote-confirmation">
          <CardContent className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#C5A880]/15 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="text-[#C5A880]" size={34} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#EADBC8] mb-4">Enquiry received</h1>
            <p className="text-[#EADBC8]/75 leading-relaxed mb-8">{CONFIRMATION}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/"><Button className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold px-8 py-6">Back to Home</Button></Link>
              <Link to="/packages"><Button variant="outline" className="border-2 border-[#C5A880] text-[#C5A880] hover:bg-[#C5A880] hover:text-[#1F1A17] font-semibold px-8 py-6">View Packages</Button></Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F1A17]">
      <Seo exactTitle="Request a Quote | Mayden Films" description="Tell Mayden Films about your event and receive a personalised quote across Cranbourne and Southeast Melbourne." path="/quote" />

      <section className="pt-36 pb-12 bg-gradient-to-br from-[#1F1A17] via-[#2A2420] to-[#1F1A17]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#EADBC8] mb-4">Check Your Event Date</h1>
          <p className="text-[#EADBC8]/70">Share your details and I’ll respond with a personalised quote within two business days. Submitting this form does not reserve your date.</p>
        </div>
      </section>

      <section className="py-16 bg-[#1A1612]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-[#2A2420] border-[#C5A880]/20">
            <CardContent className="p-6 md:p-8">
              <form onSubmit={submit} className="space-y-8" data-testid="quote-form">
                <input type="text" name="website" value={form.website} onChange={handleChange} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                <fieldset className="space-y-5">
                  <legend className="text-[#C5A880] text-sm font-semibold uppercase tracking-widest mb-2">Your details</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div><Label required>First name</Label><Input name="firstName" value={form.firstName} onChange={handleChange} required className={inputCls} data-testid="q-firstName" /></div>
                    <div><Label required>Last name</Label><Input name="lastName" value={form.lastName} onChange={handleChange} required className={inputCls} data-testid="q-lastName" /></div>
                    <div><Label required>Email</Label><Input type="email" name="email" value={form.email} onChange={handleChange} required className={inputCls} data-testid="q-email" /></div>
                    <div><Label required>Mobile number</Label><Input type="tel" name="mobile" value={form.mobile} onChange={handleChange} required placeholder="0400 000 000" className={inputCls} data-testid="q-mobile" /></div>
                  </div>
                </fieldset>

                <fieldset className="space-y-5">
                  <legend className="text-[#C5A880] text-sm font-semibold uppercase tracking-widest mb-2">Service required</legend>
                  <div>
                    <Label required>Which service(s)?</Label>
                    <p className="text-[#EADBC8]/60 text-xs mb-3">Select one or more — package options appear below.</p>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {SERVICES.map((s) => {
                        const checked = form.services.includes(s.value);
                        return (
                          <label key={s.value} className={`flex items-center gap-3 cursor-pointer rounded-lg p-3 border transition-colors ${checked ? 'border-[#C5A880] bg-[#C5A880]/10' : 'border-[#C5A880]/20 bg-[#1A1612] hover:border-[#C5A880]/40'}`}>
                            <input type="checkbox" checked={checked} onChange={() => toggleService(s.value)} className="w-4 h-4 accent-[#C5A880]" data-testid={`q-service-${s.value}`} />
                            <span className="text-[#EADBC8]/90 text-sm">{s.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {form.services.includes('glambooth') && (
                    <div>
                      <Label required>Glam Booth package</Label>
                      <Select value={form.glamTier} onValueChange={(v) => set('glamTier', v)}>
                        <SelectTrigger className={inputCls} data-testid="q-glam-tier"><SelectValue placeholder="Choose a Glam Booth package" /></SelectTrigger>
                        <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
                          {GLAM_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {needsEventType && (
                    <div data-testid="q-service-eventtype-wrap">
                      <Label required>Event or project type</Label>
                      <Select value={form.eventType} onValueChange={(v) => set('eventType', v)}>
                        <SelectTrigger className={inputCls} data-testid="q-service-eventType"><SelectValue placeholder="Select an event or project type" /></SelectTrigger>
                        <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
                          {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <p className="text-[#EADBC8]/50 text-xs mt-1.5">This filters the matching packages below and carries into your event details.</p>
                    </div>
                  )}

                  {form.services.includes('photography') && (
                    <div>
                      <Label>Photography package interest</Label>
                      {!form.eventType ? (
                        <p className="text-[#EADBC8]/55 text-sm bg-[#1A1612] border border-[#C5A880]/15 rounded-lg p-3">Choose an event/project type above to see matching photography packages. Event photography from $399; portrait sessions from $249; weddings from $1,499.</p>
                      ) : (
                        <>
                          <Select value={form.photoPackage} onValueChange={(v) => set('photoPackage', v)}>
                            <SelectTrigger className={inputCls} data-testid="q-photo-package"><SelectValue placeholder="Select a photography package" /></SelectTrigger>
                            <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
                              {photoOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {priceLabel(p)}</SelectItem>)}
                              <SelectItem value="custom-help">Help me choose / custom quote</SelectItem>
                            </SelectContent>
                          </Select>
                          {selectedPhotoPkg && (
                            <div className="bg-[#1A1612] border border-[#C5A880]/20 rounded-lg p-4 mt-3" data-testid="q-photo-estimate">
                              <p className="text-[#C5A880] text-xs font-semibold uppercase tracking-widest mb-1">Estimated price only</p>
                              <div className="flex justify-between text-[#EADBC8]"><span className="text-sm">{selectedPhotoPkg.name}</span><span className="font-semibold">{priceLabel(selectedPhotoPkg)}</span></div>
                              <ul className="text-[#EADBC8]/70 text-xs mt-2 space-y-1">
                                {selectedPhotoPkg.minutes && <li>• {hours(selectedPhotoPkg.minutes)} coverage</li>}
                                {selectedPhotoPkg.finished_images && <li>• {typeof selectedPhotoPkg.finished_images === 'number' ? `${selectedPhotoPkg.finished_images} edited images` : selectedPhotoPkg.finished_images}</li>}
                                {selectedPhotoPkg.delivery && <li>• Delivery {selectedPhotoPkg.delivery.toLowerCase()}</li>}
                              </ul>
                              {selectedPhotoPkg.scope && <p className="text-[#EADBC8]/55 text-xs mt-1.5">{selectedPhotoPkg.scope}</p>}
                              <p className="text-[#EADBC8]/45 text-xs mt-1.5">{selectedPhotoPkg.price_type === 'from' ? 'Starting price — final quote confirmed after your details.' : 'Travel and any additions are quoted separately.'}</p>
                            </div>
                          )}
                          {form.photoPackage === 'custom-help' && (
                            <p className="text-[#EADBC8]/55 text-xs mt-2">No problem — I'll recommend the best fit and confirm pricing by email.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {form.services.includes('videography') && (
                    <div>
                      <Label>Videography package interest</Label>
                      {!form.eventType ? (
                        <p className="text-[#EADBC8]/55 text-sm bg-[#1A1612] border border-[#C5A880]/15 rounded-lg p-3">Choose an event/project type above to see matching film packages. Event highlights from $899; personal films from $599; wedding films from $1,799 (enquiry-led).</p>
                      ) : (
                        <>
                          <Select value={form.videoPackage} onValueChange={(v) => set('videoPackage', v)}>
                            <SelectTrigger className={inputCls} data-testid="q-video-package"><SelectValue placeholder="Select a videography package" /></SelectTrigger>
                            <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
                              {videoOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {priceLabel(p)}</SelectItem>)}
                              <SelectItem value="custom-help">Help me choose / custom quote</SelectItem>
                            </SelectContent>
                          </Select>
                          {selectedVideoPkg && (
                            <div className="bg-[#1A1612] border border-[#C5A880]/20 rounded-lg p-4 mt-3" data-testid="q-video-estimate">
                              <p className="text-[#C5A880] text-xs font-semibold uppercase tracking-widest mb-1">Estimated price only</p>
                              <div className="flex justify-between text-[#EADBC8]"><span className="text-sm">{selectedVideoPkg.name}</span><span className="font-semibold">{priceLabel(selectedVideoPkg)}</span></div>
                              <ul className="text-[#EADBC8]/70 text-xs mt-2 space-y-1">
                                {selectedVideoPkg.minutes && <li>• Up to {hours(selectedVideoPkg.minutes)} on-site time (incl. setup &amp; pack-down)</li>}
                                {selectedVideoPkg.films && <li>• {selectedVideoPkg.films}</li>}
                              </ul>
                              {selectedVideoPkg.scope && <p className="text-[#EADBC8]/55 text-xs mt-1.5">{selectedVideoPkg.scope}</p>}
                              <p className="text-[#EADBC8]/45 text-xs mt-1.5">{selectedVideoPkg.price_type === 'from' ? 'Starting price — final quote confirmed after your details.' : 'Travel and any additions are quoted separately.'}</p>
                            </div>
                          )}
                          {form.videoPackage === 'custom-help' && (
                            <p className="text-[#EADBC8]/55 text-xs mt-2">No problem — I'll recommend the best fit and confirm pricing by email.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {['Photography', 'Videography'].map((svc) => {
                    const key = svc.toLowerCase();
                    if (!form.services.includes(key) || !form.eventType) return null;
                    return (
                      <div key={svc} className="bg-[#1A1612] border border-[#C5A880]/15 rounded-lg p-4" data-testid={`q-${key}-addons`}>
                        <p className="text-[#C5A880] text-xs font-semibold uppercase tracking-widest mb-2">{svc} optional add-ons</p>
                        <ul className="space-y-1.5">
                          {SERVICE_ADDONS[svc].map((a) => (
                            <li key={a.name} className="flex justify-between gap-4 text-sm">
                              <span className="text-[#EADBC8]/75">{a.name}</span>
                              <span className="text-[#EADBC8] font-medium whitespace-nowrap">{a.amount}</span>
                            </li>
                          ))}
                        </ul>
                        <p className="text-[#EADBC8]/45 text-xs mt-2.5">Add-ons are agreed in writing and quoted separately — they are not included in the estimate above. Travel, a $100 Sunday/public-holiday surcharge and gallery extensions (+$25 / 30 days) may also apply.</p>
                      </div>
                    );
                  })}

                  {form.services.includes('combined') && (
                    <p className="text-[#EADBC8]/70 text-sm bg-[#1A1612] border border-[#C5A880]/15 rounded-lg p-3" data-testid="q-note-combined">Combined services are individually quoted for the best overall value.</p>
                  )}

                  {(form.services.includes('combined') || form.services.length > 1) && !isPriced && (
                    <div className="p-4 bg-[#1A1612] border border-[#C5A880]/20 rounded-lg text-[#EADBC8]/80 text-sm" data-testid="custom-quote-note">
                      Multiple or combined services are individually quoted. Mayden Films will review your requirements and confirm availability and pricing by email.
                    </div>
                  )}

                  {isPriced && currentPkg && (
                    <>
                      <div className="text-[#EADBC8]/70 text-sm">{currentPkg.name} — <span className="text-[#C5A880] font-semibold">{currentPkg.price}</span>, {currentPkg.duration}.</div>
                      {(currentPkg.upgrades || []).length > 0 && (
                        <div>
                          <Label>Optional upgrades</Label>
                          <div className="space-y-2">
                            {currentPkg.upgrades.map((u) => (
                              <label key={u.name} className="flex items-start gap-3 cursor-pointer bg-[#1A1612] border border-[#C5A880]/15 rounded-lg p-3 hover:border-[#C5A880]/40 transition-colors">
                                <input type="checkbox" checked={form.selectedUpgrades.includes(u.name)} onChange={() => toggleUpgrade(u.name)} className="mt-1 w-4 h-4 accent-[#C5A880]" data-testid={`q-upgrade-${u.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                                <span className="flex-1 flex justify-between gap-2">
                                  <span className="text-[#EADBC8]/85 text-sm">{u.name}</span>
                                  <span className="text-[#C5A880] text-sm whitespace-nowrap">{u.price_label}{u.variable ? '' : ''}</span>
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Promo */}
                      <div>
                        <Label>Promo code</Label>
                        <div className="flex gap-2">
                          <Input name="promoCode" value={form.promoCode} onChange={handleChange} placeholder="Enter code" className={inputCls} data-testid="q-promoCode" />
                          <Button type="button" onClick={applyPromo} className="bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold whitespace-nowrap" data-testid="q-apply-promo">Apply</Button>
                        </div>
                        {promoMsg && (
                          <p className={`text-xs mt-1.5 flex items-center gap-1 ${appliedCode ? 'text-green-400' : 'text-red-400'}`} data-testid="q-promo-msg">
                            <Tag size={12} /> {promoMsg}
                          </p>
                        )}
                      </div>

                      {/* Estimate breakdown */}
                      {pricing && (
                        <div className="bg-[#1A1612] border border-[#C5A880]/20 rounded-lg p-4 text-sm space-y-1.5" data-testid="q-breakdown">
                          <p className="text-[#C5A880] text-xs font-semibold uppercase tracking-widest">Estimated price only</p>
                          <div className="flex justify-between text-[#EADBC8]/80"><span>Estimated subtotal</span><span>{money(pricing.eligibleSubtotal)}</span></div>
                          {pricing.discount > 0 && (
                            <div className="flex justify-between text-green-400"><span>Discount{pricing.promoName ? ` (${pricing.promoName})` : ''}</span><span>-{money(pricing.discount)}</span></div>
                          )}
                          <div className="flex justify-between text-[#EADBC8] font-semibold border-t border-[#C5A880]/15 pt-1.5"><span>Estimated total</span><span>{money(pricing.finalTotal)}</span></div>
                          <div className="flex justify-between text-[#C5A880]"><span>30% booking fee (est.)</span><span>{money(pricing.bookingFee)}</span></div>
                          {(pricing.variableItems || []).length > 0 && (
                            <p className="text-[#EADBC8]/50 text-xs pt-1">Quoted separately: {pricing.variableItems.map((v) => `${v.name} (${v.label})`).join(', ')}.</p>
                          )}
                          <p className="text-[#EADBC8]/45 text-xs pt-1">This is an estimate only. Travel &amp; any external costs are calculated separately and confirmed in your written quote.</p>
                        </div>
                      )}
                    </>
                  )}
                </fieldset>

                <fieldset className="space-y-5">
                  <legend className="text-[#C5A880] text-sm font-semibold uppercase tracking-widest mb-2">Event details</legend>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {!needsEventType && (
                      <div>
                        <Label required>Event type</Label>
                        <Select value={form.eventType} onValueChange={(v) => set('eventType', v)}>
                          <SelectTrigger className={inputCls} data-testid="q-eventType"><SelectValue placeholder="Select event type" /></SelectTrigger>
                          <SelectContent className="bg-[#2A2420] border-[#C5A880]/20 text-[#EADBC8]">
                            {EVENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    <div><Label>Event, project or subject name</Label><Input name="eventName" value={form.eventName} onChange={handleChange} className={inputCls} data-testid="q-eventName" /></div>
                    <div><Label required>Event or shoot date</Label><Input type="date" name="eventDate" value={form.eventDate} onChange={handleChange} required className={inputCls} data-testid="q-eventDate" /></div>
                    {showGuestCount && (
                      <div><Label>Estimated guest count</Label><Input type="number" name="guestCount" value={form.guestCount} onChange={handleChange} placeholder="Approx." className={inputCls} data-testid="q-guestCount" /></div>
                    )}
                    <div><Label>Venue or shoot location</Label><Input name="venueName" value={form.venueName} onChange={handleChange} className={inputCls} data-testid="q-venueName" /></div>
                    <div><Label>Venue suburb &amp; postcode</Label><Input name="venueSuburb" value={form.venueSuburb} onChange={handleChange} placeholder="e.g. Cranbourne 3977" className={inputCls} data-testid="q-venueSuburb" /></div>
                    <div><Label>Event start time</Label><Input type="text" name="startTime" value={form.startTime} onChange={handleChange} placeholder="e.g. 6:00 PM" className={inputCls} data-testid="q-startTime" /></div>
                    <div><Label>Event end time</Label><Input type="text" name="finishTime" value={form.finishTime} onChange={handleChange} placeholder="e.g. 9:00 PM" className={inputCls} data-testid="q-finishTime" /></div>
                  </div>
                  <div><Label>Additional information</Label><Textarea name="otherRequirements" value={form.otherRequirements} onChange={handleChange} rows={4} placeholder="Anything else I should know?" className={inputCls} data-testid="q-otherRequirements" /></div>
                  <div><Label>How did you hear about Mayden Films?</Label><Input name="hearAbout" value={form.hearAbout} onChange={handleChange} placeholder="Instagram, referral, Google…" className={inputCls} data-testid="q-hearAbout" /></div>
                </fieldset>

                <fieldset className="space-y-3 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.contactPermission} onChange={(e) => set('contactPermission', e.target.checked)} className="mt-1 w-4 h-4 accent-[#C5A880]" data-testid="q-contactPermission" />
                    <span className="text-[#EADBC8]/85 text-sm">I give permission for Mayden Films to contact me about this enquiry. <span className="text-[#C5A880]">*</span></span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.privacyAck} onChange={(e) => set('privacyAck', e.target.checked)} className="mt-1 w-4 h-4 accent-[#C5A880]" data-testid="q-privacyAck" />
                    <span className="text-[#EADBC8]/85 text-sm">I have read and acknowledge the <Link to="/privacy" className="text-[#C5A880] hover:underline">Privacy Policy</Link>. <span className="text-[#C5A880]">*</span></span>
                  </label>
                </fieldset>

                <Button type="submit" disabled={submitting} className="w-full bg-[#C5A880] hover:bg-[#D4BC95] text-[#1F1A17] font-semibold py-6 text-base" data-testid="q-submit">
                  {submitting ? 'Sending…' : 'Send Enquiry'}
                </Button>
                <p className="text-[#EADBC8]/40 text-xs text-center">Submitting this form does not reserve your date. A valid promo code does not confirm availability. No payment details are collected here.</p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Quote;
